/**
 * SAMPLE CONTENT of the AI Assistant conversation workspace.
 *
 * There is no assistant backend. Everything below — conversations, customer
 * names, figures, answers — is invented demo content transcribed from the
 * design file ("Sakhtar AI Assistant.dc.html" frames A–D and section 04) and
 * extended with a few plausible scripted answers. It is NEVER real tenant
 * data: every answer rendered from this file carries the «نمونه» tag.
 *
 * Rules kept here:
 *   - money is a plain, preformatted Persian display string — never a number
 *     that is summed, divided or rounded on the client;
 *   - token counts are integers;
 *   - dates are the design's relative / Jalali labels as fixed strings, no
 *     date maths;
 *   - nothing in this file imports React.
 */
import { faDigits } from '../../../lib/faDigits.ts';
import type {
  AnswerContent,
  AnswerFooter,
  ConversationBucket,
  DrillChip,
  PeriodId,
  ScopeKey,
  TableCellValue,
} from './types.ts';

export const SCOPE_LABELS: Readonly<Record<ScopeKey, string>> = {
  sales: 'فروش',
  inventory: 'انبار',
  procurement: 'تدارکات',
  accounting: 'حسابداری',
  projects: 'پروژه',
};

export const PERIOD_OPTIONS: readonly { readonly id: PeriodId; readonly label: string }[] = [
  { id: 'this-month', label: 'این ماه' },
  { id: 'last-month', label: 'ماه قبل' },
  { id: 'this-quarter', label: 'فصل جاری' },
  { id: 'fiscal-year', label: 'سال مالی' },
];

/** Sample token pool numbers shown in the rail's balance box come from `useAiPreview().tokens`. */
export const SAMPLE_LIST_NOTE = 'نمونه — بدون داده واقعی';

// ---------------------------------------------------------------------------
// Scripted answers
// ---------------------------------------------------------------------------

/** How an answer extends the context panel's «مسیر تحلیل». */
export interface SamplePath {
  /** `root` starts a new path, `entity` replaces everything below the root, `view` re-cuts the current layer. */
  readonly mode: 'root' | 'entity' | 'view';
  readonly label: string;
  /** Document focused by default (shown as the last, non-link step). */
  readonly record?: string;
}

export interface SampleAnswer {
  readonly id: string;
  /** Modules the answer reads — all must be in the caller's real scope AND selected in the composer. */
  readonly requires: readonly ScopeKey[];
  readonly content: AnswerContent;
  readonly footer: AnswerFooter;
  readonly filters: readonly string[];
  readonly sources: readonly string[];
  readonly path: SamplePath;
}

const entity = (text: string, entityId: string): TableCellValue => ({
  kind: 'link',
  text,
  action: { kind: 'entity', entityId, label: text },
});

const chip = (id: string, label: string): DrillChip => ({
  id,
  label,
  action: { kind: 'chip', chipId: id, label },
});

const TOP_CUSTOMER_ROWS = [
  ['fulad-arya', 'فولاد آریا', '۱٬۵۲۰٬۰۰۰٬۰۰۰', '۱۴', '۳۶٪'],
  ['petro-noor', 'پتروشیمی نور', '۱٬۳۵۰٬۰۰۰٬۰۰۰', '۱۱', '۳۲٪'],
  ['sanaye-pars', 'صنایع پارس', '۷۴۰٬۰۰۰٬۰۰۰', '۹', '۱۷٪'],
  ['sepehr', 'بازرگانی سپهر', '۴۱۰٬۰۰۰٬۰۰۰', '۸', '۱۰٪'],
  ['raha', 'ماشین‌سازی رها', '۲۶۰٬۰۰۰٬۰۰۰', '۶', '۵٪'],
] as const;

const topCustomerRows = TOP_CUSTOMER_ROWS.map(([id, name, sales, orders, share]) => ({
  id,
  cells: { customer: entity(name, id), sales, orders, share },
}));

/** Daily sales of شهریور, in میلیون تومان — integers that add up to the headline 4,280 (۴٬۲۸۰ میلیون). */
const DAILY_SALES = [
  109, 89, 135, 124, 81, 157, 144, 115, 132, 171, 138, 126, 85, 149, 161, 134, 120, 176, 165, 142,
  129, 114, 152, 167, 181, 136, 131, 158, 145, 140, 174,
] as const;

const MONTHS_1405 = ['فروردین', 'اردیبهشت', 'خرداد', 'تیر', 'مرداد', 'شهریور'] as const;

/** Frame B — «فروش شهریور چطور بود؟» */
const SALES_SEP: SampleAnswer = {
  id: 'sales-sep',
  requires: ['sales'],
  content: {
    text: [
      'فروش شهریور ',
      '۱۴۰۵',
      ' برابر ',
      { strong: '۴٬۲۸۰٬۰۰۰٬۰۰۰' },
      ' تومان بود؛ ۱۲٪ بیشتر از مرداد. رشد از دو مشتری صنعتی آمده و به کانال جدید وابسته نیست.',
    ],
    kpis: [
      {
        id: 'total',
        label: 'فروش کل (تومان)',
        value: '۴٬۲۸۰٬۰۰۰٬۰۰۰',
        delta: { direction: 'up', value: '۱۲٪', caption: 'نسبت به مرداد' },
      },
      {
        id: 'orders',
        label: 'تعداد سفارش',
        value: '۱۴۲',
        delta: { direction: 'up', value: '۸٪', caption: 'نسبت به مرداد' },
      },
      {
        id: 'avg',
        label: 'میانگین سفارش (تومان)',
        value: '۳۰٬۱۴۰٬۸۴۵',
        delta: { direction: 'down', value: '۳٪', caption: 'نسبت به مرداد' },
      },
    ],
    chart: {
      kind: 'line',
      id: 'daily',
      caption: 'فروش روزانهٔ شهریور (میلیون تومان)',
      ariaLabel: 'نمودار خطی فروش روزانهٔ شهریور ۱۴۰۵',
      categories: DAILY_SALES.map((_, index) => faDigits(index + 1)),
      series: [{ name: 'فروش روزانه', data: DAILY_SALES }],
      height: 190,
    },
    table: {
      columns: [
        { key: 'customer', label: 'پنج مشتری برتر' },
        { key: 'sales', label: 'فروش (تومان)', align: 'num' },
        { key: 'orders', label: 'سفارش', align: 'num' },
        { key: 'share', label: 'سهم', align: 'num' },
      ],
      rows: topCustomerRows,
      moreLabel: 'نمایش همه (۲۴ مشتری)',
    },
    drill: [
      chip('by-customer', 'به تفکیک مشتری'),
      chip('by-month', 'به تفکیک ماه'),
      chip('central-warehouse', 'فقط انبار مرکزی'),
      chip('yoy', 'مقایسه با سال قبل'),
    ],
    source: {
      lines: ['بر اساس ۱۴۲ سفارش فروش تأییدشده، ۱ تا ۳۱ شهریور ۱۴۰۵، انبار: همه.'],
      postedOnly: false,
    },
  },
  footer: { basis: 'بر اساس ۱۴۲ سفارش فروش تأییدشده، شهریور ۱۴۰۵', tokens: 1080 },
  filters: ['شهریور ۱۴۰۵', 'انبار: همه', 'سفارش تأییدشده'],
  sources: ['سفارش فروش'],
  path: { mode: 'root', label: 'فروش کل' },
};

/** Frame C — second layer, «فولاد آریا». */
const CUSTOMER_DETAIL: SampleAnswer = {
  id: 'customer-detail',
  requires: ['sales', 'accounting'],
  content: {
    text: [
      'فولاد آریا در شهریور ۱۴ سفارش به ارزش ',
      { strong: '۱٬۵۲۰٬۰۰۰٬۰۰۰' },
      ' تومان داشت و ۲۲۰٬۰۰۰٬۰۰۰ تومان مطالبهٔ باز دارد.',
    ],
    table: {
      columns: [
        { key: 'order', label: 'سفارش' },
        { key: 'amount', label: 'مبلغ (تومان)', align: 'num' },
        { key: 'status', label: 'وضعیت' },
      ],
      rows: [
        {
          id: 'so-0231',
          cells: {
            order: { kind: 'record', text: 'SO-۱۴۰۵-۰۲۳۱' },
            amount: '۴۸۰٬۰۰۰٬۰۰۰',
            status: { kind: 'badge', text: 'تسویه‌شده', tone: 'success' },
          },
        },
        {
          id: 'so-0247',
          cells: {
            order: { kind: 'record', text: 'SO-۱۴۰۵-۰۲۴۷' },
            amount: '۳۶۰٬۰۰۰٬۰۰۰',
            status: { kind: 'badge', text: 'در انتظار', tone: 'warn' },
          },
        },
        {
          id: 'so-0258',
          cells: {
            order: { kind: 'record', text: 'SO-۱۴۰۵-۰۲۵۸' },
            amount: '۲۲۰٬۰۰۰٬۰۰۰',
            status: { kind: 'badge', text: 'مطالبهٔ باز', tone: 'warn' },
          },
        },
      ],
      moreLabel: 'نمایش همه (۱۴ سفارش)',
    },
    drill: [chip('item-breakdown', 'به تفکیک کالا'), chip('receivable-aging', 'سن مطالبات')],
    source: {
      lines: ['بر اساس سفارش‌های فروش تأییدشده و مطالبات باز «فولاد آریا»، شهریور ۱۴۰۵.'],
      postedOnly: true,
    },
  },
  footer: { basis: 'سفارش فروش · فاکتور فروش · مطالبات', tokens: 760 },
  filters: ['شهریور ۱۴۰۵', 'انبار: همه', 'سفارش تأییدشده'],
  sources: ['سفارش فروش', 'فاکتور فروش', 'مطالبات'],
  path: { mode: 'entity', label: 'فولاد آریا', record: 'SO-۱۴۰۵-۰۲۳۱' },
};

/** Frame D — cross-module (sales + inventory cost + accounting). */
const CUSTOMER_PROFIT: SampleAnswer = {
  id: 'customer-profit',
  requires: ['sales', 'inventory', 'accounting'],
  content: {
    text: [
      'سودآوری هر مشتری در شهریور ۱۴۰۵. بهای تمام‌شده از انبار و سود ناخالص از تفاضل آن با فروش محاسبه شده است.',
    ],
    table: {
      columns: [
        { key: 'customer', label: 'مشتری' },
        { key: 'sales', label: 'فروش (تومان)', align: 'num' },
        { key: 'cost', label: 'بهای تمام‌شده', align: 'num' },
        { key: 'profit', label: 'سود ناخالص', align: 'num' },
        { key: 'margin', label: 'حاشیه', align: 'num' },
        { key: 'claim', label: 'مطالبهٔ باز', align: 'num' },
      ],
      rows: [
        {
          id: 'fulad-arya',
          cells: {
            customer: entity('فولاد آریا', 'fulad-arya'),
            sales: '۱٬۵۲۰٬۰۰۰٬۰۰۰',
            cost: '۱٬۱۸۵٬۰۰۰٬۰۰۰',
            profit: '۳۳۵٬۰۰۰٬۰۰۰',
            margin: '۲۲٪',
            claim: '۲۲۰٬۰۰۰٬۰۰۰',
          },
        },
        {
          id: 'petro-noor',
          cells: {
            customer: entity('پتروشیمی نور', 'petro-noor'),
            sales: '۱٬۳۵۰٬۰۰۰٬۰۰۰',
            cost: '۹۷۲٬۰۰۰٬۰۰۰',
            profit: '۳۷۸٬۰۰۰٬۰۰۰',
            margin: '۲۸٪',
            claim: '۰',
          },
        },
        {
          id: 'sanaye-pars',
          cells: {
            customer: entity('صنایع پارس', 'sanaye-pars'),
            sales: '۷۴۰٬۰۰۰٬۰۰۰',
            cost: '۶۲۹٬۰۰۰٬۰۰۰',
            profit: '۱۱۱٬۰۰۰٬۰۰۰',
            margin: '۱۵٪',
            claim: '۱۳۰٬۰۰۰٬۰۰۰',
          },
        },
        {
          id: 'sepehr',
          cells: {
            customer: entity('بازرگانی سپهر', 'sepehr'),
            sales: '۴۱۰٬۰۰۰٬۰۰۰',
            cost: '۳۶۰٬۸۰۰٬۰۰۰',
            profit: '۴۹٬۲۰۰٬۰۰۰',
            margin: '۱۲٪',
            claim: '۱۴۸٬۰۰۰٬۰۰۰',
          },
        },
      ],
      footer: {
        customer: 'جمع',
        sales: '۴٬۰۲۰٬۰۰۰٬۰۰۰',
        cost: '۳٬۱۴۶٬۸۰۰٬۰۰۰',
        profit: '۸۷۳٬۲۰۰٬۰۰۰',
        margin: '۲۲٪',
        claim: '۴۹۸٬۰۰۰٬۰۰۰',
      },
    },
    source: {
      lines: [
        'سفارش فروش تأییدشده · بهای تمام‌شدهٔ انبار.',
        'سود ناخالص = فروش − بهای تمام‌شده؛ جمع فقط چهار مشتری نمایش‌داده‌شده را شامل می‌شود.',
      ],
      postedOnly: true,
    },
  },
  footer: {
    basis: 'منبع: سفارش فروش تأییدشده · بهای تمام‌شدهٔ انبار · فقط اسناد قطعی (Posted)',
    tokens: 2460,
  },
  filters: ['شهریور ۱۴۰۵', 'انبار: همه', 'فقط اسناد قطعی'],
  sources: ['سفارش فروش', 'بهای تمام‌شدهٔ انبار', 'مطالبات'],
  path: { mode: 'root', label: 'سودآوری مشتریان' },
};

/** Suggested prompt SALES — «فروش این ماه به تفکیک مشتری». */
const SALES_BY_CUSTOMER: SampleAnswer = {
  id: 'sales-by-customer',
  requires: ['sales'],
  content: {
    text: [
      'فروش به تفکیک مشتری: ',
      { strong: 'فولاد آریا' },
      ' و ',
      { strong: 'پتروشیمی نور' },
      ' با هم ۶۸٪ فروش شهریور ۱۴۰۵ را ساخته‌اند.',
    ],
    chart: {
      kind: 'donut',
      id: 'share',
      caption: 'سهم هر مشتری از فروش (درصد)',
      ariaLabel: 'نمودار دونات سهم پنج مشتری برتر از فروش شهریور',
      valueSuffix: '٪',
      centerLabel: 'شهریور',
      height: 230,
      data: [
        { label: 'فولاد آریا', value: 36 },
        { label: 'پتروشیمی نور', value: 32 },
        { label: 'صنایع پارس', value: 17 },
        { label: 'بازرگانی سپهر', value: 10 },
        { label: 'ماشین‌سازی رها', value: 5 },
      ],
    },
    table: {
      columns: [
        { key: 'customer', label: 'مشتری' },
        { key: 'sales', label: 'فروش (تومان)', align: 'num' },
        { key: 'orders', label: 'سفارش', align: 'num' },
        { key: 'share', label: 'سهم', align: 'num' },
      ],
      rows: topCustomerRows,
      moreLabel: 'نمایش همه (۲۴ مشتری)',
    },
    drill: [chip('by-month', 'به تفکیک ماه'), chip('yoy', 'مقایسه با سال قبل')],
    source: {
      lines: ['بر اساس ۱۴۲ سفارش فروش تأییدشده، ۱ تا ۳۱ شهریور ۱۴۰۵، انبار: همه.'],
      postedOnly: false,
    },
  },
  footer: { basis: 'بر اساس ۱۴۲ سفارش فروش تأییدشده، شهریور ۱۴۰۵', tokens: 960 },
  filters: ['شهریور ۱۴۰۵', 'انبار: همه', 'سفارش تأییدشده'],
  sources: ['سفارش فروش'],
  path: { mode: 'root', label: 'فروش به تفکیک مشتری' },
};

/** Suggested prompt INVENTORY — «کالاهای زیر نقطهٔ سفارش». */
const INVENTORY_REORDER: SampleAnswer = {
  id: 'inventory-reorder',
  requires: ['inventory'],
  content: {
    text: [
      'در انبار ',
      { strong: '۷ کالا' },
      ' زیر نقطهٔ سفارش است؛ ۲ مورد از آن‌ها بحرانی‌اند (موجودی کمتر از نصف نقطهٔ سفارش).',
    ],
    kpis: [
      { id: 'below', label: 'کالای زیر نقطهٔ سفارش', value: '۷' },
      { id: 'critical', label: 'بحرانی', value: '۲' },
      { id: 'ok', label: 'کالای بدون مشکل', value: '۱۱۳' },
    ],
    table: {
      columns: [
        { key: 'item', label: 'کالا' },
        { key: 'onhand', label: 'موجودی', align: 'num' },
        { key: 'reorder', label: 'نقطهٔ سفارش', align: 'num' },
        { key: 'gap', label: 'کمبود', align: 'num' },
        { key: 'state', label: 'وضعیت' },
      ],
      rows: [
        {
          id: 'sheet',
          cells: {
            item: entity('ورق گالوانیزه ۲ میل', 'item-sheet'),
            onhand: '۴۰',
            reorder: '۱۵۰',
            gap: '۱۱۰',
            state: { kind: 'badge', text: 'بحرانی', tone: 'danger' },
          },
        },
        {
          id: 'pipe',
          cells: {
            item: entity('لولهٔ فولادی ۴ اینچ', 'item-pipe'),
            onhand: '۸۵',
            reorder: '۲۰۰',
            gap: '۱۱۵',
            state: { kind: 'badge', text: 'بحرانی', tone: 'danger' },
          },
        },
        {
          id: 'rebar',
          cells: {
            item: entity('میلگرد ۱۴', 'item-rebar'),
            onhand: '۱٬۲۰۰',
            reorder: '۲٬۰۰۰',
            gap: '۸۰۰',
            state: { kind: 'badge', text: 'نیاز به سفارش', tone: 'warn' },
          },
        },
        {
          id: 'profile',
          cells: {
            item: entity('پروفیل ۴۰×۴۰', 'item-profile'),
            onhand: '۲۱۰',
            reorder: '۳۰۰',
            gap: '۹۰',
            state: { kind: 'badge', text: 'نیاز به سفارش', tone: 'warn' },
          },
        },
        {
          id: 'angle',
          cells: {
            item: entity('نبشی ۵', 'item-angle'),
            onhand: '۶۰',
            reorder: '۱۰۰',
            gap: '۴۰',
            state: { kind: 'badge', text: 'نیاز به سفارش', tone: 'warn' },
          },
        },
      ],
      moreLabel: 'نمایش همه (۷ کالا)',
    },
    anomaly: {
      badge: 'نکتهٔ قابل توجه',
      text: [
        'مصرف «ورق گالوانیزه ۲ میل» در شهریور نسبت به میانگین سه ماه اول سال ',
        { strong: '۴۵٪' },
        ' بیشتر بوده و موجودی فعلی با این روند کفاف نمی‌دهد.',
      ],
      action: chip('item-galvanized', 'بررسی این کالا'),
    },
    source: {
      lines: ['بر اساس موجودی همهٔ انبارها و نقطهٔ سفارش ثبت‌شدهٔ هر کالا.'],
      postedOnly: false,
    },
  },
  footer: { basis: 'بر اساس موجودی همهٔ انبارها', tokens: 820 },
  filters: ['انبار: همه', 'موجودی فعلی'],
  sources: ['موجودی انبار'],
  path: { mode: 'root', label: 'کالاهای زیر نقطهٔ سفارش' },
};

/** Suggested prompt RECEIVABLES — «مطالبات معوق بالای ۶۰ روز». */
const RECEIVABLES_60: SampleAnswer = {
  id: 'receivables-60',
  requires: ['accounting'],
  content: {
    text: [
      'مطالبات معوق بالای ۶۰ روز ',
      { strong: '۱٬۱۴۰٬۰۰۰٬۰۰۰' },
      ' تومان است و به ۶ مشتری مربوط می‌شود.',
    ],
    kpis: [
      { id: 'total', label: 'جمع معوق (تومان)', value: '۱٬۱۴۰٬۰۰۰٬۰۰۰' },
      { id: 'customers', label: 'تعداد مشتری', value: '۶' },
      { id: 'oldest', label: 'قدیمی‌ترین مورد', value: '۱۳۸ روز' },
    ],
    chart: {
      kind: 'donut',
      id: 'aging',
      caption: 'سهم هر بازهٔ سنی از مطالبات معوق (درصد)',
      ariaLabel: 'نمودار دونات سن مطالبات معوق',
      valueSuffix: '٪',
      centerLabel: 'معوق',
      height: 230,
      data: [
        { label: '۶۱ تا ۹۰ روز', value: 46 },
        { label: '۹۱ تا ۱۲۰ روز', value: 31 },
        { label: 'بیش از ۱۲۰ روز', value: 23 },
      ],
    },
    table: {
      columns: [
        { key: 'customer', label: 'مشتری' },
        { key: 'balance', label: 'مانده (تومان)', align: 'num' },
        { key: 'age', label: 'سن', align: 'num' },
        { key: 'state', label: 'وضعیت' },
      ],
      rows: [
        {
          id: 'r1',
          cells: {
            customer: entity('صنایع پارس', 'sanaye-pars'),
            balance: '۴۲۰٬۰۰۰٬۰۰۰',
            age: '۱۳۸ روز',
            state: { kind: 'badge', text: 'بیش از ۱۲۰ روز', tone: 'danger' },
          },
        },
        {
          id: 'r2',
          cells: {
            customer: entity('بازرگانی سپهر', 'sepehr'),
            balance: '۳۱۰٬۰۰۰٬۰۰۰',
            age: '۹۴ روز',
            state: { kind: 'badge', text: '۹۱ تا ۱۲۰ روز', tone: 'warn' },
          },
        },
        {
          id: 'r3',
          cells: {
            customer: entity('فولاد آریا', 'fulad-arya'),
            balance: '۱۸۰٬۰۰۰٬۰۰۰',
            age: '۷۲ روز',
            state: { kind: 'badge', text: '۶۱ تا ۹۰ روز', tone: 'warn' },
          },
        },
        {
          id: 'r4',
          cells: {
            customer: entity('ماشین‌سازی رها', 'raha'),
            balance: '۱۳۰٬۰۰۰٬۰۰۰',
            age: '۶۸ روز',
            state: { kind: 'badge', text: '۶۱ تا ۹۰ روز', tone: 'warn' },
          },
        },
        {
          id: 'r5',
          cells: {
            customer: entity('پتروشیمی نور', 'petro-noor'),
            balance: '۶۰٬۰۰۰٬۰۰۰',
            age: '۶۲ روز',
            state: { kind: 'badge', text: '۶۱ تا ۹۰ روز', tone: 'warn' },
          },
        },
      ],
      moreLabel: 'نمایش همه (۶ مشتری)',
    },
    source: {
      lines: ['بر اساس مانده‌های باز حساب‌های دریافتنی، تا امروز.'],
      postedOnly: true,
    },
  },
  footer: { basis: 'مطالبات باز، تا امروز', tokens: 640 },
  filters: ['سن بالای ۶۰ روز', 'فقط اسناد قطعی'],
  sources: ['مطالبات'],
  path: { mode: 'root', label: 'مطالبات معوق' },
};

/** Suggested prompt TREASURY — «موجودی نقد و بانک امروز». */
const CASH_TODAY: SampleAnswer = {
  id: 'cash-today',
  requires: ['accounting'],
  content: {
    text: ['موجودی نقد و بانک امروز جمعاً ', { strong: '۲٬۳۶۰٬۰۰۰٬۰۰۰' }, ' تومان است.'],
    kpis: [
      { id: 'total', label: 'جمع نقد و بانک (تومان)', value: '۲٬۳۶۰٬۰۰۰٬۰۰۰' },
      { id: 'bank', label: 'حساب‌های بانکی (تومان)', value: '۲٬۳۱۰٬۰۰۰٬۰۰۰' },
      { id: 'cash', label: 'صندوق (تومان)', value: '۵۰٬۰۰۰٬۰۰۰' },
    ],
    chart: {
      kind: 'bar',
      id: 'accounts',
      caption: 'موجودی هر حساب (میلیون تومان)',
      ariaLabel: 'نمودار میله‌ای موجودی حساب‌های بانکی و صندوق',
      height: 210,
      data: [
        { label: 'بانک ملی', value: 1420 },
        { label: 'بانک ملت', value: 680 },
        { label: 'بانک پاسارگاد', value: 210 },
        { label: 'صندوق', value: 50 },
      ],
    },
    source: {
      lines: ['بر اساس مانده حساب‌های بانکی و صندوق، تا پایان امروز.'],
      postedOnly: true,
    },
  },
  footer: { basis: 'مانده حساب‌های نقد و بانک، امروز', tokens: 520 },
  filters: ['امروز', 'فقط اسناد قطعی'],
  sources: ['حساب‌های بانکی', 'صندوق'],
  path: { mode: 'root', label: 'نقد و بانک' },
};

/** Suggested prompt PROJECTS — «تسک‌های عقب‌افتادهٔ من». */
const MY_TASKS: SampleAnswer = {
  id: 'my-tasks',
  requires: ['projects'],
  content: {
    text: [
      'شما ',
      { strong: '۵ تسک' },
      ' عقب‌افتاده دارید؛ قدیمی‌ترین آن ۹ روز از سررسید گذشته است.',
    ],
    table: {
      columns: [
        { key: 'task', label: 'تسک' },
        { key: 'project', label: 'پروژه' },
        { key: 'late', label: 'تأخیر', align: 'num' },
        { key: 'state', label: 'وضعیت' },
      ],
      rows: [
        {
          id: 't1',
          cells: {
            task: 'تأیید نقشهٔ اجرایی فاز دوم',
            project: 'ساختمان اداری آرمان',
            late: '۹ روز',
            state: { kind: 'badge', text: 'در جریان', tone: 'info' },
          },
        },
        {
          id: 't2',
          cells: {
            task: 'استعلام قیمت میلگرد',
            project: 'ساختمان اداری آرمان',
            late: '۶ روز',
            state: { kind: 'badge', text: 'شروع‌نشده', tone: 'neutral' },
          },
        },
        {
          id: 't3',
          cells: {
            task: 'صورت‌وضعیت شمارهٔ ۳',
            project: 'کارخانهٔ سپنتا',
            late: '۴ روز',
            state: { kind: 'badge', text: 'در جریان', tone: 'info' },
          },
        },
        {
          id: 't4',
          cells: {
            task: 'هماهنگی تحویل پروفیل',
            project: 'کارخانهٔ سپنتا',
            late: '۲ روز',
            state: { kind: 'badge', text: 'شروع‌نشده', tone: 'neutral' },
          },
        },
        {
          id: 't5',
          cells: {
            task: 'بازبینی برنامهٔ زمانی',
            project: 'انبار مرکزی',
            late: '۱ روز',
            state: { kind: 'badge', text: 'در جریان', tone: 'info' },
          },
        },
      ],
    },
    source: { lines: ['بر اساس تسک‌های باز واگذارشده به شما.'], postedOnly: false },
  },
  footer: { basis: 'تسک‌های باز واگذارشده به شما', tokens: 380 },
  filters: ['واگذارشده به من', 'سررسید گذشته'],
  sources: ['تسک‌های پروژه'],
  path: { mode: 'root', label: 'تسک‌های عقب‌افتاده' },
};

/** Suggested prompt DAILY — «گزارش روزانهٔ مدیریتی». */
const DAILY_REPORT: SampleAnswer = {
  id: 'daily-report',
  requires: ['sales', 'accounting'],
  content: {
    text: [
      'گزارش روزانه: فروش امروز ',
      { strong: '۱۵۶٬۰۰۰٬۰۰۰' },
      ' تومان است. مطالبات معوق بالای ۶۰ روز همچنان بزرگ‌ترین نکتهٔ مراقبتی است.',
    ],
    kpis: [
      {
        id: 'today',
        label: 'فروش امروز (تومان)',
        value: '۱۵۶٬۰۰۰٬۰۰۰',
        delta: { direction: 'up', value: '۶٪', caption: 'نسبت به دیروز' },
      },
      { id: 'overdue', label: 'مطالبات معوق (تومان)', value: '۱٬۱۴۰٬۰۰۰٬۰۰۰' },
      { id: 'cash', label: 'نقد و بانک (تومان)', value: '۲٬۳۶۰٬۰۰۰٬۰۰۰' },
    ],
    drill: [
      {
        id: 'p-recv',
        label: 'مطالبات معوق بالای ۶۰ روز',
        action: { kind: 'prompt', text: 'مطالبات معوق بالای ۶۰ روز' },
      },
      {
        id: 'p-sales',
        label: 'فروش شهریور چطور بود؟',
        action: { kind: 'prompt', text: 'فروش شهریور چطور بود؟' },
      },
    ],
    source: {
      lines: ['بر اساس سفارش‌های فروش امروز، مطالبات باز و مانده حساب‌های نقد و بانک.'],
      postedOnly: true,
    },
  },
  footer: { basis: 'فروش، مطالبات و نقد و بانک، امروز', tokens: 1450 },
  filters: ['امروز', 'فقط اسناد قطعی'],
  sources: ['سفارش فروش', 'مطالبات', 'حساب‌های بانکی'],
  path: { mode: 'root', label: 'گزارش روزانه' },
};

// ---- drill-down children ---------------------------------------------------

const BY_MONTH: SampleAnswer = {
  id: 'by-month',
  requires: ['sales'],
  content: {
    text: [
      'روند ماهانهٔ فروش از فروردین تا شهریور ۱۴۰۵. شهریور با ',
      { strong: '۴٬۲۸۰٬۰۰۰٬۰۰۰' },
      ' تومان بالاترین ماه این دوره است.',
    ],
    chart: {
      kind: 'line',
      id: 'monthly',
      caption: 'فروش ماهانه (میلیون تومان)',
      ariaLabel: 'نمودار خطی فروش ماهانه از فروردین تا شهریور ۱۴۰۵',
      categories: MONTHS_1405,
      series: [{ name: 'فروش ماهانه', data: [2980, 3240, 3110, 3620, 3810, 4280] }],
      height: 220,
    },
    drill: [chip('yoy', 'مقایسه با سال قبل')],
    source: { lines: ['بر اساس سفارش‌های فروش تأییدشدهٔ ۶ ماه اول ۱۴۰۵.'], postedOnly: false },
  },
  footer: { basis: 'سفارش فروش تأییدشده، فروردین تا شهریور ۱۴۰۵', tokens: 540 },
  filters: ['فروردین تا شهریور ۱۴۰۵', 'انبار: همه', 'سفارش تأییدشده'],
  sources: ['سفارش فروش'],
  path: { mode: 'view', label: 'به تفکیک ماه' },
};

const YOY: SampleAnswer = {
  id: 'yoy',
  requires: ['sales'],
  content: {
    text: ['فروش شهریور ۱۴۰۵ نسبت به شهریور ۱۴۰۴ حدود ', { strong: '۲۰٪' }, ' بیشتر است.'],
    kpis: [
      {
        id: 'now',
        label: 'شهریور ۱۴۰۵ (تومان)',
        value: '۴٬۲۸۰٬۰۰۰٬۰۰۰',
        delta: { direction: 'up', value: '۲۰٪', caption: 'نسبت به شهریور ۱۴۰۴' },
      },
      { id: 'before', label: 'شهریور ۱۴۰۴ (تومان)', value: '۳٬۵۶۰٬۰۰۰٬۰۰۰' },
    ],
    chart: {
      kind: 'bar',
      id: 'yoy',
      caption: 'فروش شهریور دو سال متوالی (میلیون تومان)',
      ariaLabel: 'نمودار میله‌ای مقایسهٔ فروش شهریور ۱۴۰۴ و ۱۴۰۵',
      height: 200,
      data: [
        { label: 'شهریور ۱۴۰۴', value: 3560 },
        { label: 'شهریور ۱۴۰۵', value: 4280 },
      ],
    },
    source: {
      lines: ['بر اساس سفارش‌های فروش تأییدشدهٔ شهریور ۱۴۰۴ و شهریور ۱۴۰۵.'],
      postedOnly: false,
    },
  },
  footer: { basis: 'سفارش فروش تأییدشده، شهریور ۱۴۰۴ و ۱۴۰۵', tokens: 610 },
  filters: ['شهریور ۱۴۰۵ در برابر ۱۴۰۴', 'سفارش تأییدشده'],
  sources: ['سفارش فروش'],
  path: { mode: 'view', label: 'مقایسه با سال قبل' },
};

const CENTRAL_WAREHOUSE: SampleAnswer = {
  id: 'central-warehouse',
  requires: ['sales', 'inventory'],
  content: {
    text: [
      'فقط خروجی «انبار مرکزی»: ',
      { strong: '۳٬۱۰۰٬۰۰۰٬۰۰۰' },
      ' تومان، یعنی ۷۲٪ فروش شهریور از این انبار تأمین شده است.',
    ],
    kpis: [
      { id: 'sales', label: 'فروش از انبار مرکزی (تومان)', value: '۳٬۱۰۰٬۰۰۰٬۰۰۰' },
      { id: 'orders', label: 'تعداد سفارش', value: '۹۸' },
      { id: 'share', label: 'سهم از فروش کل', value: '۷۲٪' },
    ],
    source: {
      lines: ['بر اساس ۹۸ سفارش فروش تأییدشده با خروج از «انبار مرکزی»، شهریور ۱۴۰۵.'],
      postedOnly: false,
    },
  },
  footer: { basis: 'سفارش فروش تأییدشده، انبار مرکزی، شهریور ۱۴۰۵', tokens: 700 },
  filters: ['شهریور ۱۴۰۵', 'انبار: مرکزی', 'سفارش تأییدشده'],
  sources: ['سفارش فروش', 'موجودی انبار'],
  path: { mode: 'view', label: 'انبار مرکزی' },
};

const ITEM_BREAKDOWN: SampleAnswer = {
  id: 'item-breakdown',
  requires: ['sales'],
  content: {
    text: ['فروش «فولاد آریا» در شهریور به تفکیک کالا؛ ورق گالوانیزه بیشترین سهم را دارد.'],
    chart: {
      kind: 'donut',
      id: 'item-share',
      caption: 'سهم هر کالا از فروش فولاد آریا (درصد)',
      ariaLabel: 'نمودار دونات سهم کالاها در فروش فولاد آریا',
      valueSuffix: '٪',
      centerLabel: 'فولاد آریا',
      height: 220,
      data: [
        { label: 'ورق گالوانیزه ۲ میل', value: 45 },
        { label: 'لولهٔ فولادی ۴ اینچ', value: 34 },
        { label: 'میلگرد ۱۴', value: 21 },
      ],
    },
    table: {
      columns: [
        { key: 'item', label: 'کالا' },
        { key: 'qty', label: 'مقدار', align: 'num' },
        { key: 'amount', label: 'مبلغ (تومان)', align: 'num' },
      ],
      rows: [
        {
          id: 'i1',
          cells: { item: 'ورق گالوانیزه ۲ میل', qty: '۲۸۰ ورق', amount: '۶۹۰٬۰۰۰٬۰۰۰' },
        },
        {
          id: 'i2',
          cells: { item: 'لولهٔ فولادی ۴ اینچ', qty: '۴۲۰ شاخه', amount: '۵۱۰٬۰۰۰٬۰۰۰' },
        },
        { id: 'i3', cells: { item: 'میلگرد ۱۴', qty: '۱۸ تن', amount: '۳۲۰٬۰۰۰٬۰۰۰' } },
      ],
      footer: { item: 'جمع', qty: '', amount: '۱٬۵۲۰٬۰۰۰٬۰۰۰' },
    },
    source: {
      lines: ['بر اساس سطرهای ۱۴ سفارش فروش تأییدشدهٔ «فولاد آریا»، شهریور ۱۴۰۵.'],
      postedOnly: false,
    },
  },
  footer: { basis: 'سطرهای سفارش فروش، فولاد آریا', tokens: 560 },
  filters: ['شهریور ۱۴۰۵', 'مشتری: فولاد آریا', 'سفارش تأییدشده'],
  sources: ['سفارش فروش'],
  path: { mode: 'view', label: 'به تفکیک کالا' },
};

const RECEIVABLE_AGING: SampleAnswer = {
  id: 'receivable-aging',
  requires: ['accounting'],
  content: {
    text: [
      'مطالبهٔ باز «فولاد آریا» ',
      { strong: '۲۲۰٬۰۰۰٬۰۰۰' },
      ' تومان است؛ بیش از نیمی از آن هنوز در بازهٔ جاری قرار دارد.',
    ],
    chart: {
      kind: 'donut',
      id: 'claim-aging',
      caption: 'سن مطالبهٔ باز (درصد)',
      ariaLabel: 'نمودار دونات سن مطالبهٔ باز فولاد آریا',
      valueSuffix: '٪',
      centerLabel: 'مطالبه',
      height: 220,
      data: [
        { label: 'جاری (تا ۳۰ روز)', value: 55 },
        { label: '۳۱ تا ۶۰ روز', value: 27 },
        { label: '۶۱ تا ۹۰ روز', value: 18 },
      ],
    },
    source: {
      lines: ['بر اساس مانده‌های باز حساب دریافتنی «فولاد آریا»، تا امروز.'],
      postedOnly: true,
    },
  },
  footer: { basis: 'مطالبات باز، فولاد آریا', tokens: 430 },
  filters: ['مشتری: فولاد آریا', 'فقط اسناد قطعی'],
  sources: ['مطالبات'],
  path: { mode: 'view', label: 'سن مطالبات' },
};

const ITEM_GALVANIZED: SampleAnswer = {
  id: 'item-galvanized',
  requires: ['inventory'],
  content: {
    text: [
      'مصرف ماهانهٔ «ورق گالوانیزه ۲ میل» از فروردین ',
      'روندی صعودی دارد؛ موجودی فعلی ',
      { strong: '۴۰ ورق' },
      ' و نقطهٔ سفارش ۱۵۰ ورق است.',
    ],
    chart: {
      kind: 'line',
      id: 'sheet-usage',
      caption: 'مصرف ماهانه (ورق)',
      ariaLabel: 'نمودار خطی مصرف ماهانهٔ ورق گالوانیزه ۲ میل',
      categories: MONTHS_1405,
      series: [{ name: 'مصرف', data: [310, 295, 340, 420, 455, 457] }],
      height: 210,
    },
    source: {
      lines: ['بر اساس حرکت‌های خروج انبار برای «ورق گالوانیزه ۲ میل»، ۶ ماه اول ۱۴۰۵.'],
      postedOnly: false,
    },
  },
  footer: { basis: 'حرکت‌های انبار، ورق گالوانیزه ۲ میل', tokens: 350 },
  filters: ['فروردین تا شهریور ۱۴۰۵', 'کالا: ورق گالوانیزه ۲ میل'],
  sources: ['حرکت انبار'],
  path: { mode: 'entity', label: 'ورق گالوانیزه ۲ میل' },
};

export const SAMPLE_ANSWERS: Readonly<Record<string, SampleAnswer>> = Object.fromEntries(
  [
    SALES_SEP,
    CUSTOMER_DETAIL,
    CUSTOMER_PROFIT,
    SALES_BY_CUSTOMER,
    INVENTORY_REORDER,
    RECEIVABLES_60,
    CASH_TODAY,
    MY_TASKS,
    DAILY_REPORT,
    BY_MONTH,
    YOY,
    CENTRAL_WAREHOUSE,
    ITEM_BREAKDOWN,
    RECEIVABLE_AGING,
    ITEM_GALVANIZED,
  ].map((answer) => [answer.id, answer]),
);

/** `chip.id` → answer. */
export const CHIP_ANSWERS: Readonly<Record<string, string>> = {
  'by-customer': 'sales-by-customer',
  'by-month': 'by-month',
  'central-warehouse': 'central-warehouse',
  yoy: 'yoy',
  'item-breakdown': 'item-breakdown',
  'receivable-aging': 'receivable-aging',
  'item-galvanized': 'item-galvanized',
};

/** `entityId` → answer; every other entity has no authored sample. */
export const ENTITY_ANSWERS: Readonly<Record<string, string>> = {
  'fulad-arya': 'customer-detail',
};

// ---------------------------------------------------------------------------
// Empty state — the six suggested prompts (frame A)
// ---------------------------------------------------------------------------

export interface SuggestedPrompt {
  readonly id: string;
  /** Persian category caption (the design prints an English frame-label here; see ChatWorkspace). */
  readonly tag: string;
  readonly text: string;
  readonly answerId: string;
}

export const SUGGESTED_PROMPTS: readonly SuggestedPrompt[] = [
  { id: 'sales', tag: 'فروش', text: 'فروش این ماه به تفکیک مشتری', answerId: 'sales-by-customer' },
  { id: 'inventory', tag: 'انبار', text: 'کالاهای زیر نقطهٔ سفارش', answerId: 'inventory-reorder' },
  {
    id: 'receivables',
    tag: 'مطالبات',
    text: 'مطالبات معوق بالای ۶۰ روز',
    answerId: 'receivables-60',
  },
  { id: 'treasury', tag: 'خزانه', text: 'موجودی نقد و بانک امروز', answerId: 'cash-today' },
  { id: 'projects', tag: 'پروژه', text: 'تسک‌های عقب‌افتادهٔ من', answerId: 'my-tasks' },
  { id: 'daily', tag: 'گزارش روزانه', text: 'گزارش روزانهٔ مدیریتی', answerId: 'daily-report' },
];

// ---------------------------------------------------------------------------
// Conversation rail
// ---------------------------------------------------------------------------

export interface SampleConversation {
  readonly id: string;
  readonly title: string;
  readonly pinned: boolean;
  readonly bucket: ConversationBucket;
  /** The single prompt → answer pair the conversation contains. */
  readonly prompt: string;
  readonly answerId: string;
}

export const SAMPLE_CONVERSATIONS: readonly SampleConversation[] = [
  {
    id: 'c-daily',
    title: 'گزارش روزانهٔ مدیریتی',
    pinned: true,
    bucket: 'today',
    prompt: 'گزارش روزانهٔ مدیریتی',
    answerId: 'daily-report',
  },
  {
    id: 'c-sales-sep',
    title: 'فروش شهریور چطور بود؟',
    pinned: false,
    bucket: 'today',
    prompt: 'فروش شهریور چطور بود؟',
    answerId: 'sales-sep',
  },
  {
    id: 'c-receivables',
    title: 'مطالبات معوق بالای ۶۰ روز',
    pinned: false,
    bucket: 'today',
    prompt: 'مطالبات معوق بالای ۶۰ روز',
    answerId: 'receivables-60',
  },
  {
    id: 'c-profit',
    title: 'سودآوری هر مشتری',
    pinned: false,
    bucket: 'week',
    prompt: 'سودآوری هر مشتری',
    answerId: 'customer-profit',
  },
  {
    id: 'c-reorder',
    title: 'کالاهای زیر نقطهٔ سفارش',
    pinned: false,
    bucket: 'week',
    prompt: 'کالاهای زیر نقطهٔ سفارش',
    answerId: 'inventory-reorder',
  },
];
